import IconButton from "@material-ui/core/IconButton";
// import DeleteIcon from "@material-ui/icons/Delete";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { PAGINATE_BY } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import CornermarkBulkDeleteDialog from "@saleor/cornermarks/components/CornermarkBulkDeleteDialog";
import CornermarkListPage from "@saleor/cornermarks/components/CornermarkListPage";
import { useCornermarkBulkDeleteMutation } from "@saleor/cornermarks/mutations";
import { useCornermarkListQuery } from "@saleor/cornermarks/queries";
import {
  cornermarkAddUrl,
  cornermarkListUrl,
  CornermarkListUrlDialog,
  CornermarkListUrlQueryParams,
  cornermarkUrl
} from "@saleor/cornermarks/urls";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { useIntl } from "react-intl";

import useBulkActions from "../../../hooks/useBulkActions";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface CornermarkListProps {
  params: CornermarkListUrlQueryParams;
}

const CornermarkList: React.FC<CornermarkListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch } = useCornermarkListQuery({
    variables: queryVariables
  });

  const [
    cornermarkBulkDelete,
    cornermarkBulkDeleteOpts
  ] = useCornermarkBulkDeleteMutation({
    onCompleted: data => {
      if (data.cornerMarkBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Cornermark successfully delete",
            description: "deleted multiple cornermark"
          })
        });
        reset();
        refetch();
      }
    }
  });
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [openModal, closeModal] = createDialogActionHandlers<
    CornermarkListUrlDialog,
    CornermarkListUrlQueryParams
  >(navigate, cornermarkListUrl, params);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: cornermarkListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      cornermarkListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(cornermarkListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.cornerMarks.pageInfo),
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, cornermarkListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");
  return (
    <>
      <CornermarkListPage
        cornermarks={maybe(() =>
          data.cornerMarks.edges?.map(edge => edge.node)
        )}
        toatlCount={maybe(() => data.cornerMarks.totalCount, 0)}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={loading || cornermarkBulkDeleteOpts.loading}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        isChecked={isSelected}
        onAdd={() => navigate(cornermarkAddUrl())}
        onAll={resetFilters}
        onBack={() => navigate(configurationMenuUrl)}
        onFilterChange={changeFilters}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onRowClick={id => () => navigate(cornermarkUrl(id))}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        pageInfo={pageInfo}
        selected={listElements.length}
        sort={getSortParams(params)}
        tabs={tabs?.map(tab => tab.name)}
        toggle={toggle}
        toggleAll={toggleAll}
        toolbar={
          <IconButton
            color="primary"
            // onClick={() =>
            //   openModal("remove", {
            //     ids: listElements
            //   })
            // }
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        }
      />
      <CornermarkBulkDeleteDialog
        confirmButtonState={cornermarkBulkDeleteOpts.status}
        open={params.action === "remove" && maybe(() => params.ids.length > 0)}
        onConfirm={() =>
          cornermarkBulkDelete({ variables: { ids: params.ids } })
        }
        onClose={closeModal}
        quantity={maybe(() => params.ids.length)}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default CornermarkList;
